import { useEffect, useRef } from 'react';

import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type { DependencyOutcome } from '../dependencyHealthAnalytics.ts';
import type { PageAgentContributor } from '../utils/normalizePageAgentContributors.ts';

import type { UsePageAgentContributorsResult } from './usePageAgentContributors.ts';
import { useDependencyReadHealth, type DependencyRead } from './useDependencyReadHealth.ts';

const errorOutcome = (error: Error): DependencyOutcome => {
	if (isUnauthorizedError(error)) {
		return 'denied';
	}
	if (error.name === 'AbortError') {
		return 'cancelled';
	}
	return 'failed';
};

export type AgentContributorsHealthOptions = Pick<
	UsePageAgentContributorsResult,
	'availability' | 'error'
> & {
	contentId: string | undefined;
	agents: PageAgentContributor[];
};

const contributorRead = ({
	availability,
	error,
	agents,
}: AgentContributorsHealthOptions): DependencyRead => {
	if (availability === 'disabled') {
		return { status: 'idle' };
	}
	if (availability === 'loading') {
		return { status: 'loading' };
	}
	if (error) {
		return { status: 'settled', outcome: errorOutcome(error), result: agents, error };
	}
	if (availability === 'unavailable') {
		return { status: 'settled', outcome: 'unavailable', result: agents };
	}
	return {
		status: 'settled',
		outcome: agents.length === 0 ? 'empty' : 'succeeded',
		result: agents,
	};
};

export const useAgentContributorsHealth = (options: AgentContributorsHealthOptions): void => {
	useDependencyReadHealth({
		dependency: 'agentContributors',
		operation: 'read',
		identity: options.contentId ?? '',
		read: contributorRead(options),
	});
};

export type AgentViewersHealthOptions = {
	contentId: string;
	accessEnabled: boolean;
	access: { canView: boolean; loading: boolean; error: Error | null | undefined };
	loading: boolean;
	current: boolean;
	viewers: readonly unknown[];
	error: Error | undefined;
};

const accessRead = (
	enabled: boolean,
	access: AgentViewersHealthOptions['access'],
): DependencyRead => {
	if (!enabled) {
		return { status: 'idle' };
	}
	if (access.loading) {
		return { status: 'loading' };
	}
	if (access.error) {
		return {
			status: 'settled',
			outcome: errorOutcome(access.error),
			result: access.canView,
			error: access.error,
		};
	}
	return {
		status: 'settled',
		outcome: access.canView ? 'succeeded' : 'denied',
		result: access.canView,
	};
};

const viewerRead = (
	enabled: boolean,
	observedLoading: boolean,
	options: AgentViewersHealthOptions,
): DependencyRead => {
	if (!enabled) {
		return { status: 'idle' };
	}
	if (options.loading) {
		return { status: 'loading' };
	}
	if (options.error && (options.current || observedLoading)) {
		// The shared viewer reader also returns permission failures in HTTP 200 responses.
		const permissionError =
			options.error.message.includes('PermissionException') ||
			options.error.message.includes('403');
		return {
			status: 'settled',
			outcome: permissionError ? 'denied' : errorOutcome(options.error),
			result: options.viewers,
			error: options.error,
		};
	}
	if (!options.current) {
		if (observedLoading) {
			return { status: 'settled', outcome: 'unavailable', result: undefined };
		}
		return { status: 'idle' };
	}
	return {
		status: 'settled',
		outcome: options.viewers.length === 0 ? 'empty' : 'succeeded',
		result: options.viewers,
	};
};

export const useAgentViewersHealth = (options: AgentViewersHealthOptions): void => {
	const { cloudId, edition } = useSessionData();
	// A false canView also represents a disabled shared reader, not a permission decision.
	const accessEnabled =
		options.accessEnabled && !!options.contentId && !!cloudId && edition === 'PREMIUM';
	const viewersEnabled = accessEnabled && options.access.canView;
	const identity = JSON.stringify([cloudId, options.contentId]);
	const loadingIdentity = useRef<string>();
	useEffect(() => {
		if (!viewersEnabled) {
			loadingIdentity.current = undefined;
		} else if (options.loading) {
			loadingIdentity.current = identity;
		}
	}, [identity, viewersEnabled, options.loading]);
	useDependencyReadHealth({
		dependency: 'agentViewerAccess',
		operation: 'read',
		identity,
		read: accessRead(accessEnabled, options.access),
	});
	useDependencyReadHealth({
		dependency: 'agentViewers',
		operation: 'read',
		identity,
		read: viewerRead(viewersEnabled, loadingIdentity.current === identity, options),
	});
};
